import { SOUND_FILES, SoundKey } from './soundRegistry'

class SoundEngine {
  private ctx: AudioContext | null = null
  private buffers = new Map<SoundKey, AudioBuffer>()
  private masterGain: GainNode | null = null
  private unlocked = false

  async init() {
    if (this.ctx) return

    this.ctx = new (window.AudioContext ||
      (window as any).webkitAudioContext)()

    this.masterGain = this.ctx.createGain()
    this.masterGain.gain.value = 1
    this.masterGain.connect(this.ctx.destination)

    if (this.ctx.state === 'suspended') {
      await this.ctx.resume()
    }

    await this.preloadAll()
    this.unlocked = true
  }

  private async preloadAll() {
    if (!this.ctx) return

    const entries = Object.entries(SOUND_FILES) as [
      SoundKey,
      string
    ][]

    await Promise.all(
      entries.map(async ([key, url]) => {
        const res = await fetch(url)
        const arrayBuffer = await res.arrayBuffer()
        const buffer = await this.ctx!.decodeAudioData(arrayBuffer)
        this.buffers.set(key, buffer)
      })
    )
  }

  play(key: SoundKey, volume = 1) {
    if (!this.ctx || !this.unlocked) return

    const buffer = this.buffers.get(key)
    if (!buffer) return

    const startSource = () => {
      const source = this.ctx!.createBufferSource()
      const gainNode = this.ctx!.createGain()
      gainNode.gain.value = volume
      source.buffer = buffer
      source.connect(gainNode)
      gainNode.connect(this.masterGain!)
      source.start(0)
    }

    if (this.ctx.state === 'suspended') {
      this.ctx.resume().then(startSource).catch(() => {})
    } else {
      startSource()
    }
  }

  setMasterVolume(volume: number) {
    if (this.masterGain) {
      this.masterGain.gain.value = volume
    }
  }
}

export const soundEngine = new SoundEngine()